import React, { useState } from "react";
import Select from "react-select"
import Popup from "reactjs-popup";
import { mergeSelectStyles } from "../../shared/darkSelectStyles";
import axios from "axios";
import { EditorState } from "draft-js";
import { stateFromHTML } from "draft-js-import-html";
import { stateToHTML } from "draft-js-export-html";

import RichEditor from "../RichEditor/components/RichEditor";
import { useRentalInvoiceEditContext } from "./RentalInvoiceEditContainer";
import RentalInvoiceInfoCard from "./RentalInvoiceInfoCard";
import RentalInvoiceItemsCard from "./RentalInvoiceItemsCard";
import RentalInvoiceDepositsCard from "./RentalInvoiceDepositsCard";
import RentalInvoiceTaxFeesCard from "./RentalInvoiceTaxFeesCard";
import RentalInvoiceTotalsCard from "./RentalInvoiceTotalsCard";
import SendRentalInvoiceModal from "./SendRentalInvoiceModal";
import { toastError } from "../../shared/toastHelper";
import { documentNoun } from "./statusLabels";

const templateSaveMenuContentStyle = {
  textAlign: "left",
  border: "none",
  borderRadius: "8px",
  boxShadow: "0 0 1px 1px rgba(0,0,0,0.02), 0 4px 6px 1px rgba(0,0,0,0.06)",
  width: "215px",
  padding: "0px",
  background: "var(--sl-bg-primary, #fff)"
}

const templateSelectStyles = mergeSelectStyles({ container: (base) => ({ ...base, width: 300 }) });

const RentalInvoiceEdit = () => {
  const {
    team,
    csrfToken,
    rentalInvoice,
    rentalInvoiceFormState,
    setRentalInvoiceFormState,
    rentalInvoiceTemplates,
  } = useRentalInvoiceEditContext();

  const [isCreatingTemplate, setIsCreatingTemplate] = useState(false);
  const [isUpdatingTemplate, setIsUpdatingTemplate] = useState(false);
  const [templateToSaveTitle, setTemplateToSaveTitle] = useState("");
  const [showSendModal, setShowSendModal] = useState(false);
  const [notesEditorState, setNotesEditorState] = useState(
    EditorState.createWithContent(stateFromHTML(rentalInvoiceFormState.notes || ""))
  );
  const [termsEditorState, setTermsEditorState] = useState(
    EditorState.createWithContent(stateFromHTML(rentalInvoiceFormState.terms_and_conditions || ""))
  );

  const createTemplate = (e) => {
    e.preventDefault();

    if (!templateToSaveTitle || templateToSaveTitle.length === 0) return;

    axios.defaults.headers.common["X-CSRF-TOKEN"] = csrfToken;
    axios.post(`/teams/${team.id}/rental_invoices/${rentalInvoice.id}/save_as_template`, {
      title: templateToSaveTitle
    })
    .then(() => {
      window.location.reload();
    })
    .catch(error => {
      toastError(error.response?.data?.join(", ") || "Failed to create template");
    });
  };

  const updateTemplate = (e) => {
    e.preventDefault();

    const template = rentalInvoiceTemplates.find(t => t.id === rentalInvoiceFormState.template_id);

    const itemEdits = {};
    (rentalInvoiceFormState.rental_invoice_items_attributes || []).forEach((entry) => {
      if (entry && entry.id != null) itemEdits[entry.id] = entry;
    });

    const items = rentalInvoice.rental_invoice_items
      .map((item) => {
        const edits = itemEdits[item.id] || {};
        if (edits._destroy) return null;
        return {
          actual_total: edits.actual_total ?? item.actual_total,
          category: edits.category ?? item.category,
          cost: edits.cost ?? item.cost,
          cost_type: edits.cost_type ?? item.cost_type,
          description: edits.description ?? item.description,
          position: edits.position ?? item.position,
          quantity: edits.quantity ?? item.quantity,
          title: edits.title ?? item.title,
        };
      })
      .filter(Boolean);

    const depositEdits = {};
    (rentalInvoiceFormState.deposits_attributes || []).forEach((entry) => {
      if (entry && entry.id != null) depositEdits[entry.id] = entry;
    });

    const deposits = rentalInvoice.deposits
      .map((deposit) => {
        const edits = depositEdits[deposit.id] || {};
        if (edits._destroy) return null;
        return {
          deposit_type: edits.deposit_type ?? deposit.deposit_type,
          due_date_type: edits.due_date_type ?? deposit.due_date_type,
          paid: edits.paid ?? deposit.paid,
          relative_due_date: edits.relative_due_date ?? deposit.relative_due_date,
          specific_due_date: edits.specific_due_date ?? deposit.specific_due_date,
          value: edits.value ?? deposit.value,
        };
      })
      .filter(Boolean);

    axios.defaults.headers.common["X-CSRF-TOKEN"] = csrfToken;
    axios.put(`/teams/${team.id}/rental_invoices/${template.id}`, {
      rental_invoice: {
        notes: rentalInvoiceFormState.notes,
        terms_and_conditions: rentalInvoiceFormState.terms_and_conditions,
        attendance_count: rentalInvoiceFormState.attendance_count,
        discount: rentalInvoiceFormState.discount,
        payee_name: rentalInvoiceFormState.payee_name,
        title: templateToSaveTitle,
        status: rentalInvoiceFormState.status,
        rental_invoice_items_attributes: [
          ...template.rental_invoice_items.map(item => ({
            id: item.id,
            _destroy: true
          })),
          ...items,
        ],
        deposits_attributes: [
          ...template.deposits.map(deposit => ({
            id: deposit.id,
            _destroy: true
          })),
          ...deposits,
        ]
      }
    })
    .then(() => {
      window.location.reload();
    })
    .catch(error => {
      toastError(error.response?.data?.join(", ") || "Failed to update template");
    });
  };

  const templateChanged = (e) => {
    axios.defaults.headers.common["X-CSRF-TOKEN"] = csrfToken;
    axios.put(`/teams/${team.id}/rental_invoices/${rentalInvoice.id}`, {
      rental_invoice: {
        template_id: e.value
      }
    })
    .then(response => {
      window.location.reload();
    })
    .catch(error => {
      toastError(error.response?.data?.join(", ") || "Failed to update template");
    });
  };

  return (
    <div style={{paddingTop: 32, paddingLeft: 10, display: "flex", flexDirection: "column"}}>
      <div className="row no-gutters" style={{ alignItems: "center", gap: 12, marginBottom: 24 }}>
        <i className="far fa-calendar setlist-text-xl figma-text placeholder" />
        <i className="far fa-chevron-right setlist-text-sm figma-text senary" />
        <a href={`/teams/${team.id}/confirms/${rentalInvoice.calendar_event?.id}#finances`} className="setlist-text-sm setlist-font-semibold figma-text tertiary">
          {rentalInvoice.calendar_event?.title}
        </a>
      </div>
      <div style={{ marginBottom: 24 }}>
        <div className="setlist-display-xs setlist-font-semibold" style={{ marginBottom: 12 }}>Rental Event</div>
        <div className="row no-gutters" style={{ justifyContent: "space-between", alignItems: "center", gap: 20 }}>
          <div className="row no-gutters" style={{ gap: 12, alignItems: "center" }}>
            <Select
              options={rentalInvoiceTemplates.map(template => ({ value: template.id, label: template.title }))}
              value={rentalInvoiceTemplates.map(template => ({ value: template.id, label: template.title })).find(template => (template.value === rentalInvoiceFormState.template_id))}
              onChange={templateChanged}
              placeholder="Select a Template"
              styles={templateSelectStyles}
            />
            {isCreatingTemplate || isUpdatingTemplate ? (
              <form onSubmit={isUpdatingTemplate ? updateTemplate : createTemplate}>
                <div className="input-group">
                  <input type="text"
                         className="form-control"
                         value={templateToSaveTitle}
                         onChange={e => setTemplateToSaveTitle(e.target.value)}
                         onFocus={e => e.target.select()}
                         style={{
                           "border": "1px solid var(--sl-border-secondary, #e6e6e6)",
                           "borderRight": "none",
                           "borderTopLeftRadius": "4px",
                           "borderBottomLeftRadius": "4px"
                         }}
                         placeholder="Enter template name..." />
                  <div className="input-group-append">
                    <button className="btn figma-text link"
                            style={{
                              "background": "var(--sl-bg-primary, #fff)",
                              "border": "1px solid var(--sl-border-secondary, #e6e6e6)",
                              "borderLeft": "none",
                              "borderTopRightRadius": "4px",
                              "borderBottomRightRadius": "4px"
                            }}
                            disabled={!templateToSaveTitle || templateToSaveTitle.length === 0}
                            type="submit">
                      <strong>Save</strong>
                    </button>
                  </div>
                </div>
              </form>
            ) : (
              <Popup
                trigger={
                  <a href="#"
                     onClick={e => e.preventDefault()}
                     className="figma-text link">
                    <strong>
                      Save as...
                      <i className="far fa-save ml-2"></i>
                    </strong>
                  </a>
                }
                onOpen={e => e.preventDefault()}
                arrow={false}
                offsetY={10}
                position="bottom center"
                contentStyle={templateSaveMenuContentStyle}
                closeOnDocumentClick>
                {close => (
                  <div>
                    <div className="row">
                      <div className="col">
                        <ul className="list-group offer-inline-menu highlight-primary"
                            style={{"fontSize": "14px"}}>
                          {rentalInvoiceFormState.template_id ? (
                            <li className="list-group-item pl-3 pb-2 pt-3">
                              <a href="#"
                                 className="text-dark"
                                 onClick={(e) => {
                                   e.preventDefault();
                                   const template = rentalInvoiceTemplates.find(t => t.id === rentalInvoiceFormState.template_id);
                                   setTemplateToSaveTitle(template.title);
                                   setIsUpdatingTemplate(true);
                                   close();
                                 }}>
                                <span style={{"fontWeight": "normal"}}>
                                  Save to current template
                                </span>
                              </a>
                            </li>
                          ) : null}
                          <li className="list-group-item pl-3 pt-2 pb-3">
                            <a href="#"
                               className="text-dark"
                               onClick={e => {
                                 e.preventDefault();
                                 setIsCreatingTemplate(true);
                                 close();
                               }}>
                              <span style={{"fontWeight": "normal"}}>
                                Save as new template
                              </span>
                            </a>
                          </li>
                        </ul>
                      </div>
                    </div>
                  </div>
                )}
              </Popup>
            )}
          </div>
          <div className="row no-gutters" style={{ gap: 12 }}>
            <button className="btn btn-setlist-secondary" onClick={() => { window.open(`/teams/${team.id}/rental_invoices/${rentalInvoice.id}/pdf`, "_blank"); }}>
              <i className="far fa-download mr-2"></i>
              Download PDF
            </button>
            <button
              className="btn btn-setlist-primary"
              onClick={() => setShowSendModal(true)}
            >
              <i className="far fa-paper-plane mr-2"></i>
              Send {documentNoun(rentalInvoiceFormState.status)}
            </button>
          </div>
        </div>
      </div>
      <SendRentalInvoiceModal
        csrfToken={csrfToken}
        team={team}
        rentalInvoice={rentalInvoice}
        status={rentalInvoiceFormState.status}
        isOpen={showSendModal}
        onClose={() => setShowSendModal(false)}
        onSuccess={() => setShowSendModal(false)}
      />
      <RentalInvoiceInfoCard />
      <RentalInvoiceItemsCard />
      <RentalInvoiceTaxFeesCard />
      <RentalInvoiceDepositsCard />
      <RentalInvoiceTotalsCard />
      <div className="card">
        <div className="card-body">
          <div className="setlist-text-lg setlist-font-semibold figma-text secondary">Notes</div>
          <div style={{ marginTop: 12 }}>
            <RichEditor
              editorState={notesEditorState}
              onEditorStateChange={(editorState) => {
                setNotesEditorState(editorState);
                setRentalInvoiceFormState({
                  ...rentalInvoiceFormState,
                  notes: stateToHTML(editorState.getCurrentContent())
                });
              }}
            />
          </div>
        </div>
      </div>
      <div className="card">
        <div className="card-body">
          <div className="setlist-text-lg setlist-font-semibold figma-text secondary">Terms and Conditions</div>
          <div style={{ marginTop: 12 }}>
            <RichEditor
              editorState={termsEditorState}
              onEditorStateChange={(editorState) => {
                setTermsEditorState(editorState);
                setRentalInvoiceFormState({
                  ...rentalInvoiceFormState,
                  terms_and_conditions: stateToHTML(editorState.getCurrentContent())
                });
              }}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default RentalInvoiceEdit;
